/**
 * Phase 2E.2 — client-side impersonation state.
 *
 * A root admin holding `tenants.impersonate` can act as another tenant.
 * The backend (middleware/tenant_scoping.py, Phase 2E.1) reads the
 * `X-Override-Tenant-Id` request header and swaps the effective tenant
 * for the duration of that request. This module owns the client half:
 * which tenant we are currently acting as, and nothing else.
 *
 * WHY sessionStorage AND NOT localStorage
 * ---------------------------------------
 * The session token lives in localStorage because it is an identity that
 * should survive closing the browser. Impersonation is the opposite: a
 * support session that should NOT. sessionStorage survives a refresh (so
 * you don't lose your place mid-task) but dies with the tab, so you
 * cannot return the next morning still silently acting as a customer.
 *
 * It is also per-tab, which is a feature rather than an accident — one
 * tab can be root and another can be acting as a tenant, and each tab's
 * banner tells the truth about that tab. Under localStorage the two tabs
 * would share one value and at least one banner would be lying.
 *
 * WHY THE NAME IS STORED ALONGSIDE THE ID
 * ---------------------------------------
 * The banner has to render "Acting as Dexter Laboratories" on first
 * paint, including immediately after a refresh. Storing only the id
 * would mean a fetch before the banner could name the tenant, and a
 * window where the user is impersonating with nothing on screen saying
 * so. The id is the only thing ever sent to the server; the name is
 * presentation only.
 *
 * FAIL-CLOSED
 * -----------
 * Every read is wrapped. If sessionStorage is unavailable (Safari
 * private mode, a locked-down browser policy) or the stored value is
 * corrupt, we behave as though impersonation is off. The failure mode of
 * a bad read must be "you are yourself", never "you are silently someone
 * else".
 */

const STORAGE_KEY = 'boss_impersonated_tenant';

/** The header the backend reads. Exported so callers can't typo it. */
export const IMPERSONATION_HEADER = 'X-Override-Tenant-Id';

/**
 * The tenant currently being impersonated, or null.
 * @returns {{tenant_id: string, name: string} | null}
 */
export function getImpersonation() {
  let raw;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Corrupt value — drop it rather than leaving a landmine for the
    // next read.
    clearImpersonation();
    return null;
  }

  // A stored object without a usable tenant_id is the same as no
  // impersonation, and is also cleared: sending an empty header would
  // be refused by the middleware anyway, and a banner with no name is
  // worse than no banner.
  if (!parsed || typeof parsed.tenant_id !== 'string' || !parsed.tenant_id) {
    clearImpersonation();
    return null;
  }

  return {
    tenant_id: parsed.tenant_id,
    name: typeof parsed.name === 'string' && parsed.name ? parsed.name : parsed.tenant_id,
  };
}

/**
 * Begin acting as a tenant.
 * @param {string} tenantId — the target `tenant_id`.
 * @param {string} [name] — display name for the banner.
 * @returns {boolean} true if the state was stored.
 */
export function setImpersonation(tenantId, name) {
  if (typeof tenantId !== 'string' || !tenantId) return false;
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ tenant_id: tenantId, name: name || tenantId }),
    );
    return true;
  } catch {
    return false;
  }
}

/** Stop acting as a tenant. Safe to call when not impersonating. */
export function clearImpersonation() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing we can do, and nothing that should throw upward */
  }
}

/** @returns {boolean} */
export function isImpersonating() {
  return getImpersonation() !== null;
}

/**
 * The tenant id to send, or null when not impersonating.
 * @returns {string | null}
 */
export function getImpersonationTenantId() {
  const current = getImpersonation();
  return current ? current.tenant_id : null;
}

/**
 * Header object to merge into an outbound request — `{}` when not
 * impersonating, so callers can spread it unconditionally.
 *
 * This is the ONLY place the header name and value are paired. Every
 * request path in lib/api.js goes through here; if a fifth request path
 * is ever added, it should call this rather than reconstructing the
 * header, and lib/__tests__/impersonation.test.js asserts that each
 * existing path actually sends it.
 *
 * @returns {Record<string, string>}
 */
export function impersonationHeaders() {
  const tenantId = getImpersonationTenantId();
  return tenantId ? { [IMPERSONATION_HEADER]: tenantId } : {};
}

/**
 * Apply the header to a live XMLHttpRequest.
 *
 * `apiFormRequest`, `apiDownload` and `apiDownloadRich` in lib/api.js
 * drive XHR directly instead of going through safeXhr, so they need to
 * set the header rather than merge an object. Kept here so all four
 * request paths share one definition of "am I impersonating".
 *
 * @param {XMLHttpRequest} xhr
 */
export function applyImpersonationHeader(xhr) {
  const tenantId = getImpersonationTenantId();
  if (tenantId && xhr && typeof xhr.setRequestHeader === 'function') {
    xhr.setRequestHeader(IMPERSONATION_HEADER, tenantId);
  }
}
